/* eslint-disable @next/next/no-img-element */
import { RefObject, useEffect, useRef, useState } from "react"
import { HoverSkew } from "../HoverSkew"
import { AppIcons, LargeHeading, Paragraph, Section } from "../misc"
import { ColorBlur } from "../ColorBlur"
import useIsMobile from "app/core/hooks/useIsMobile"
import useWindowSize from "app/core/hooks/useWindowSize"

export const useScrollAnimation: (ref: RefObject<HTMLDivElement>) => {
  percentage: number
  isVisible: boolean
} = (ref) => {
  const [state, setState] = useState({
    percentage: 1,
    isVisible: false,
  })

  useEffect(() => {
    const handleScroll = () => {
      const body = document.body
      const html = document.documentElement

      const height = Math.max(
        body.scrollHeight,
        body.offsetHeight,
        html.clientHeight,
        html.scrollHeight,
        html.offsetHeight
      )

      let scrollPercentage =
        ref.current && ref.current.getBoundingClientRect().top / window.innerHeight

      scrollPercentage = scrollPercentage || 0
      scrollPercentage = Math.max(0, Math.min(scrollPercentage, 1))

      let percentage = scrollPercentage
      let isVisible = scrollPercentage < 0.75

      setState({ percentage, isVisible })
      console.log(ref)
    }

    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [state, ref])

  return state
}

export const Customers = ({ animateOnScroll = true }: { animateOnScroll?: boolean }) => {
  const ref = useRef<HTMLDivElement>(null)
  const { isVisible: _isVisible } = useScrollAnimation(ref)
  const windowSize = useWindowSize()
  const [cols, setCols] = useState(10)
  useEffect(() => {
    if (windowSize?.width) {
      if (windowSize.width < 768) {
        setCols(6)
      } else {
        setCols(10)
      }
    }
  }, [windowSize])

  const isVisible = animateOnScroll ? _isVisible : true

  const [hoverIndex, setHoverIndex] = useState(-1)

  return (
    <Section className="text-center">
      <ColorBlur opacity={isVisible ? 0.25 : 0.0} />
      <div className="flex flex-col gap-1 text-center items-center pb-10 relative z-1">
        <LargeHeading>{`Trusted by the world's finest apps`}</LargeHeading>
        <Paragraph className="!max-w-[750px] !text-opacity-60">{`Nearly all Superwall customers see significant boosts in revenue within a few months of integrating.`}</Paragraph>
      </div>
      <div
        className="flex flex-col gap-[10px] md:gap-[14px] lg:gap-[18px] max-w-5xl text-left transition-all duration-[0.4s]"
        ref={ref}
        style={{
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? "scale(1)" : "scale(0.9)",
          transitionDelay: isVisible ? "0s" : "0.3s",
        }}
      >
        <HoverSkew className="grid grid-cols-6 md:grid-cols-10 items-end justify-center !mb-0 !p-3">
          {uniq(AppIcons).map((icon, index) => (
            <div
              key={`app-icon-${index}`}
              className={`relative p-[10%] md:p-[12%] ${index >= 24 ? "hidden md:block" : ""}`}
            >
              <img
                className={`pointer-events-none rounded-[24%] shadow-xl transition-all`}
                // onMouseEnter={() => setHoverIndex(index)}
                // onMouseLeave={() => setHoverIndex(-1)}
                style={{
                  // pointerEvents: "none",
                  transitionDelay: `${
                    Math.abs((index % cols) - ((cols * 1.0) / 2 - 0.5)) * 0.07 +
                    Math.floor(index / cols) * 0.04
                  }s`,
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible
                    ? `scale(1)`
                    : `scale(0.9) translateX(${
                        ((index % cols) - ((cols * 1.0) / 2 - 0.5)) * 20
                      }px) translateY(10px)`,
                  transitionDuration: "0.4s",
                }}
                src={icon.url}
                alt="app-icon"
              ></img>
            </div>
          ))}
        </HoverSkew>
        <div className="text-center">
          <div
            className="flex flex-wrap sm:flex-nowrap sm:flex-row gap-[10px] md:gap-[14px] lg:gap-[18px] justify-center sm:items-center"
            style={{
              transition: "all 0.5s",
            }}
          >
            <div
              style={{
                transition: "opacity 0.3s ease-in-out 0.1s, transform 0.3s ease-in-out 0.1s",
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? "translateY(0)" : "translateY(40px)",
              }}
              className={`delay-0 sm:w-1/3 lg:w-1/5`}
            >
              <HoverSkew className="px-2 py-3 md:px-3 md:py-5 w-full">
                <LargeHeading
                  className="text-brand-primary !mb-0 !text-3xl md:!text-5xl lg:!text-8xl lg:scale-90"
                  style={{ lineHeight: "1.3" }}
                >
                  1B
                </LargeHeading>
                <Paragraph className="!mb-1 px-2 lg:px-0 text-[10px] md:!text-base lg:!text-lg uppercase tracking-widest font-medium lg:whitespace-nowrap">
                  <span className="hidden lg:inline">End </span>
                  Users
                </Paragraph>
              </HoverSkew>
            </div>
            <div
              style={{
                transition: "opacity 0.3s ease-in-out 0.2s, transform 0.3s ease-in-out 0.2s",
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? "translateY(0)" : "translateY(40px)",
              }}
              className={`flex-grow sm:w-1/3 lg:w-2/5`}
            >
              <HoverSkew className={`px-2 py-3 md:px-3 md:py-5`}>
                <LargeHeading
                  className="text-brand-primary !mb-0 !text-3xl md:!text-5xl lg:!text-8xl lg:scale-90"
                  style={{ lineHeight: "1.3" }}
                >
                  30M
                </LargeHeading>
                <Paragraph className="!mb-1 text-[10px] md:!text-base lg:!text-lg uppercase tracking-widest font-medium lg:whitespace-nowrap">
                  <span className="block lg:hidden">Paywalls / mo</span>
                  <span className="hidden lg:block">Monthly Paywalls</span>
                </Paragraph>
              </HoverSkew>
            </div>
            <div
              style={{
                transition: "opacity 0.3s ease-in-out 0.3s, transform 0.3s ease-in-out 0.3s",
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? "translateY(0)" : "translateY(40px)",
              }}
              className={`flex-grow sm:w-1/3 lg:w-2/5`}
            >
              <HoverSkew className={`px-2 py-3 md:px-3 md:py-5`}>
                <LargeHeading
                  className="text-brand-primary !mb-0 !text-3xl md:!text-5xl lg:!text-8xl lg:scale-90"
                  style={{ lineHeight: "1.3" }}
                >
                  700K
                </LargeHeading>
                <Paragraph className="!mb-1 text-[10px] md:!text-base lg:!text-lg uppercase tracking-widest font-medium lg:whitespace-nowrap">
                  <span className="block lg:hidden">Conversions / mo</span>
                  <span className="hidden lg:block">Monthly Conversions</span>
                </Paragraph>
              </HoverSkew>
            </div>
          </div>
        </div>
      </div>
    </Section>
  )
}

// returns uniq values in array using filter
const uniq = (arr) => arr.filter((v, i) => arr.indexOf(v) === i)
